import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, TextField, InputAdornment, Button, Tooltip, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CampaignIcon from '@mui/icons-material/Campaign';
import AdvertiserCampaignCard from './AdvertiserCampaignCard';
import CreateCampaignModal from './CreateCampaignModal';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import {
  getCampaigns, createCampaign, updateCampaign, deleteCampaign,
} from '../../api/advertiserApi';
import { submitApproval } from '../../api/approvalApi';
import { useToast } from '../../context/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import StatusTabs from '../approval/StatusTabs';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../../utils/versionedContent';
import { signalRService } from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const AdvertiserCampaignList = ({ advertiser, onSelectCampaign, onCampaignUpdated, overrideSpaceId }) => {
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/advertisers', 'advertiser-edit');
  const isParentLocked = advertiser.approvalStatus === 'pending' || advertiser.approvalStatus === 'rejected';
  const searchPlaceholder  = useLocalText('Search campaigns…');
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tabValue, setTabValue] = useState('live');
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [proposeEditMode, setProposeEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [proposeDeleteTarget, setProposeDeleteTarget] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(() =>
    getCampaigns(advertiser.id, overrideSpaceId)
      .then(setCampaigns)
      .catch(() => {}),
  [advertiser.id, overrideSpaceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    const handleDecision = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.CAMPAIGN) load();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
  }, [load]);

  useEffect(() => {
    const handleWithdrawn = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.CAMPAIGN) load();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
  }, [load]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(v), 250);
  };

  // The dates the campaign has RIGHT NOW. Whether its dates can still move depends on these, not on the dates in the
  // form: a rejected proposal that is re-edited opens with its proposed dates.
  const lockBasis = useMemo(() => {
    const live = editTarget ? campaigns.find((c) => c.id === editTarget.id) : null;
    return live ? { startDate: live.startDate, endDate: live.endDate, contentStatus: live.contentStatus } : null;
  }, [campaigns, editTarget]);

  const versioned = useMemo(() => buildVersionedList(campaigns), [campaigns]);
  const counts    = useMemo(() => countVersionedTabs(campaigns),  [campaigns]);

  const filtered = useMemo(() => {
    let result = filterVersionedList(versioned, tabValue);
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return result;
    return result.filter((c) =>
      c.localizedName?.values?.some((v) => v.value.toLowerCase().includes(q))
    );
  }, [versioned, debouncedSearch, tabValue]);

  const handleSave = async (data) => {
    try {
      if (proposeEditMode) {
        const contentName = data.localizedName?.values?.[0]?.value || `Campaign ${editTarget.id}`;
        await submitApproval({
          contentType: CONTENT_TYPES.CAMPAIGN,
          contentId: editTarget.id,
          contentName: contentName,
          actionType: 'edit',
          proposedData: JSON.stringify(data),
          proposedSnapshot: JSON.stringify(data),
        });
        toast.success('Edit submitted for approval!');
        setProposeEditMode(false);
        setEditTarget(null);
        setModalOpen(false);
        await load();
        flashTab('pending');
      } else if (editTarget) {
        const updated = await updateCampaign(advertiser.id, editTarget.id, data);
        setEditTarget(null);
        await load();
        flashTab('draft');
        onCampaignUpdated?.(updated);
      } else {
        await createCampaign(advertiser.id, data);
        setEditTarget(null);
        await load();
        flashTab('draft');
        toast.success('Campaign created and moved to Draft.');
      }
    } catch (err) {
      throw err;
    }
  };

  const handleEdit = (campaign, proposeMode = false) => {
    setEditTarget(campaign);
    setModalReadOnly(false);
    setProposeEditMode(proposeMode);
    setModalOpen(true);
  };

  const handleEditRejected = (proposedSnap, liveId) => {
    setEditTarget({ ...proposedSnap, id: liveId });
    setProposeEditMode(true);
    setModalReadOnly(false);
    setModalOpen(true);
  };

  const handleView = (campaign) => {
    setEditTarget(campaign);
    setModalReadOnly(true);
    setProposeEditMode(false);
    setModalOpen(true);
  };

  const handleDelete = (campaign, proposeMode = false) => {
    if (proposeMode) setProposeDeleteTarget(campaign);
    else setDeleteTarget(campaign);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCampaign(advertiser.id, deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch {
      setDeleteTarget(null);
    }
  };

  const confirmProposeDelete = async () => {
    if (!proposeDeleteTarget) return;
    try {
      const contentName = proposeDeleteTarget.localizedName?.values?.[0]?.value || `Campaign ${proposeDeleteTarget.id}`;
      await submitApproval({
        contentType: CONTENT_TYPES.CAMPAIGN,
        contentId: proposeDeleteTarget.id,
        contentName: contentName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteTarget(null);
      await load();
      flashTab('pending');
    } catch {
      setProposeDeleteTarget(null);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditTarget(null);
    setModalReadOnly(false);
    setProposeEditMode(false);
  };

  return (
    <Box>
      {isParentLocked && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          This advertiser has a {advertiser.approvalStatus} approval. Campaigns cannot be added or modified until the approval is resolved.
        </Alert>
      )}

      <StatusTabs items={campaigns} counts={counts} value={tabValue} onChange={setTabValue} sx={{ mb: 1 }} highlightTab={highlight.tab} highlightToken={highlight.token} />

      {/* Toolbar */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#85a5c6' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 240 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: 'white',
                '& fieldset': { borderColor: '#dde6f0' },
                '&:hover fieldset': { borderColor: '#5383b3' },
                '&.Mui-focused fieldset': { borderColor: '#0a326d' },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 600 }}>
            {filtered.length} {filtered.length === 1 ? <LocalText>campaign</LocalText> : <LocalText>campaigns</LocalText>}
          </Typography>
        </Box>

        {canEdit && (
          <Tooltip title={isParentLocked ? `Advertiser has a ${advertiser.approvalStatus} approval — resolve it first` : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isParentLocked}
                onClick={(e) => { e.currentTarget.blur(); setModalOpen(true); }}
                sx={{
                  bgcolor: '#0a326d',
                  color: 'white',
                  borderRadius: '12px',
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  fontSize: 13,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
                  '&:hover': { bgcolor: '#0d3d85', boxShadow: '0 6px 16px rgba(10,50,109,0.35)' },
                  '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
                }}
              >
                <LocalText>New Campaign</LocalText>
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Empty state */}
      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <CampaignIcon sx={{ fontSize: 56, color: '#c8d8ea', mb: 1.5 }} />
          <Typography variant="body1" sx={{ color: '#85a5c6', fontWeight: 500 }}>
            {debouncedSearch ? <LocalText>No campaigns match your search.</LocalText> : <LocalText>No campaigns yet. Add one!</LocalText>}
          </Typography>
        </Box>
      )}

      {/* Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          gap: 2.5,
        }}
      >
        {filtered.map((c) => (
          <AdvertiserCampaignCard
            key={c._viewType === 'snapshot' ? `snap-${c._liveId}` : c.id}
            campaign={c}
            onSelect={onSelectCampaign}
            onEdit={handleEdit}
            onView={handleView}
            onDelete={handleDelete}
            onSuccess={(targetTab) => { load(); if (targetTab) flashTab(targetTab); }}
            showActions={canEdit}
            onEditRejected={canEdit ? handleEditRejected : undefined}
            parentLocked={isParentLocked}
          />
        ))}
      </Box>

      <CreateCampaignModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        initial={editTarget}
        readOnly={modalReadOnly}
        advertiserId={advertiser.id}
        approvalMode={proposeEditMode}
        lockBasis={lockBasis}
      />

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        severity="danger"
        title="Delete Campaign"
        message={`Delete "${deleteTarget?.localizedName?.values?.[0]?.value ?? 'this campaign'}"?`}
        subtitle="This will also delete all ads under this campaign."
        confirmLabel="Delete"
      />

      <ConfirmModal
        open={Boolean(proposeDeleteTarget)}
        onCancel={() => setProposeDeleteTarget(null)}
        onConfirm={confirmProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message={`Submit a delete request for "${proposeDeleteTarget?.localizedName?.values?.[0]?.value ?? 'this campaign'}"? The content remains active until an approver approves.`}
        confirmLabel="Submit for Approval"
      />
    </Box>
  );
};

export default AdvertiserCampaignList;
